import React, { useEffect, useReducer, useCallback, createContext, useContext, Children } from 'react';
import tw from 'twin.macro';
import throttle from 'lodash/throttle';
import { motion } from 'framer-motion';
import useSwipe from '~/libs/hooks/useSwipe';
import { InView } from 'react-intersection-observer';

const carouselReducer = (state, action, itemCount) => {
  const { active, desired } = state;
  switch (action.type) {
    case 'jump':
      return { ...state, desired: action.desired };
    case 'prev':
      return { ...state, desired: (active - 1 + itemCount) % itemCount };
    case 'next':
      return { ...state, desired: (active + 1) % itemCount };
    case 'done':
      return { ...state, offset: NaN, active: desired };
    case 'drag':
      return { ...state, offset: action.offset };
    default:
      return state;
  }
};

const CarouselContext = createContext();

const Carousel = ({ interval = 3000, duration = 1000, children, ...rest }) => {
  const slides = [],
    controls = [];
  Children.toArray(children).forEach((c) => (c.type == Carousel.Slide ? slides.push(c) : controls.push(c)));
  const slideCount = slides.length;

  const initialState = { offset: 0, desired: 0, active: 0 };
  const memoizedReducer = useCallback((state, action) => carouselReducer(state, action, slideCount), [slideCount]);
  const [state, dispatch] = useReducer(memoizedReducer, initialState);
  const { active, desired, offset } = state;

  // action dispatchers
  const next = () => dispatch({ type: 'next' });
  const prev = () => dispatch({ type: 'prev' });
  const done = () => dispatch({ type: 'done' });
  const drag = (offset) => dispatch({ type: 'drag', offset });
  const jump = (desired) => dispatch({ type: 'jump', desired });

  // Updating state after transition
  useEffect(() => {
    const id = setTimeout(done, duration);
    return () => clearTimeout(id);
  }, [desired]);

  // Slide change timer
  useEffect(() => {
    if (interval > 0) {
      const id = setTimeout(next, interval);
      return () => clearTimeout(id);
    }
  }, [offset, active, desired, interval]);

  // Handling swipes
  const onSwipe = useCallback(({ deltaX }) => drag(deltaX), []);
  const onSwipeEnd = useCallback(({ deltaX }, event) => {
    if (Math.abs(deltaX) >= event?.target?.clientWidth / 3) {
      deltaX > 0 ? prev() : next();
    } else {
      drag(0);
    }
  }, []);
  const dragHandlers = useSwipe(throttle(onSwipe, 50), onSwipeEnd, rest);

  // Calculating styles for transition
  const animate = { x: 0 };
  const transition = { duration: 0 };
  const totalItems = slideCount + 2;
  const width = `${100 * totalItems}%`;
  const left = `-${100 * (active + 1)}%`;

  if (desired !== active) {
    const diff = Math.abs(active - desired);
    const wrap = diff == slideCount - 1;
    const dist = wrap ? 1 : diff;
    const dir = Math.sign((active - desired) * (wrap ? -1 : 1));
    animate.x = `${(dir * dist * 100) / totalItems}%`;
    transition.duration = duration / 1000;
  } else if (!isNaN(offset)) {
    if (offset !== 0) {
      animate.x = offset;
    } else {
      transition.duration = duration / 1000;
    }
  }

  const renderSlides = () => {
    return React.Children.map(slides, (child, index) => {
      const children = child?.props?.children || {};
      return typeof children?.props?.handleInViewBanner === 'function' ? (
        <InView
          className="flex-1"
          as="div"
          trackVisibility={true}
          delay={800}
          threshold={0.7}
          onChange={(inView) => {
            desired === index && inView && children?.props?.id && children?.props?.handleInViewBanner({ bannerId: children?.props?.id });
          }}
        >
          {child}
        </InView>
      ) : (
        <div className="flex-1">{child}</div>
      );
    });
  };

  return (
    <CarouselContext.Provider value={{ next, prev, jump, active, desired }}>
      <div css={tw`relative`} {...rest}>
        <div className="w-full relative overflow-hidden cursor-pointer" {...dragHandlers}>
          <motion.div {...{ animate, transition }} css={[tw`relative flex transform`, { width, left }]}>
            {slides[slideCount - 1]}
            {renderSlides()}
            {slides[0]}
          </motion.div>
        </div>
        {controls}
      </div>
    </CarouselContext.Provider>
  );
};

const Slide = ({ children, ...rest }) => (
  <div className="flex-1" {...rest}>
    {children}
  </div>
);

const Control = ({ children }) => {
  const { next, prev, jump, active, desired } = useContext(CarouselContext);
  return typeof children === 'function' ? children({ next, prev, jump, active, desired }) : children;
};

Carousel.Slide = Slide;
Carousel.Control = Control;

export default Carousel;
